import { db, holisticFunctions } from '@/server';
import firebase from 'firebase/app';
import Vue from 'vue';
import Vuex from 'vuex';
import { firestoreAction, vuexfireMutations } from 'vuexfire';
import { getters } from './getters';
import { createDefaultState, State } from './State';
import FieldPath = firebase.firestore.FieldPath;
import FieldValue = firebase.firestore.FieldValue;
import DocumentReference = firebase.firestore.DocumentReference;

Vue.use(Vuex);

const updateUnmapped = async ({
  mapping,
  lookupCollection,
  unmappedCollection,
}: {
  mapping: { [key: string]: string };
  lookupCollection: string;
  unmappedCollection: string;
}) => {
  const updateOperations = Object.entries(mapping).reduce(
    (previous, [key, value]) => {
      previous.push(escapePeriodsInDataPoint(key));
      previous.push(value);
      return previous;
    },
    [] as Array<FieldPath | string>,
  );
  runUpdateOperations(
    db.collection(lookupCollection).doc('default'),
    updateOperations,
  );

  const deleteOperations = Object.keys(mapping).reduce(
    (previous, key) => {
      previous.push(escapePeriodsInDataPoint(key));
      previous.push(firebase.firestore.FieldValue.delete());
      return previous;
    },
    [] as Array<FieldPath | FieldValue>,
  );
  runUpdateOperations(
    db.collection(unmappedCollection).doc('default'),
    deleteOperations,
  );
};

const escapePeriodsInDataPoint = (key: string) => new FieldPath(key);

const runUpdateOperations = (
  doc: DocumentReference,
  operations: Array<string | FieldPath | FieldValue>,
) => doc.update(operations[0], operations[1], ...operations.slice(2));

export default new Vuex.Store<State>({
  state: createDefaultState(),

  getters,

  mutations: {
    ...vuexfireMutations,
  },

  actions: {
    bindClients: firestoreAction(({ bindFirestoreRef }) =>
      bindFirestoreRef('clients', db.collection('clients').orderBy('name'), {
        maxRefDepth: 0,
      }),
    ),
    unbindClients: firestoreAction(({ unbindFirestoreRef }) =>
      unbindFirestoreRef('clients'),
    ),
    bindClientReport: firestoreAction(({ bindFirestoreRef }, { clientId }) =>
      bindFirestoreRef(
        'clientReport',
        db
          .collection('widgets')
          .where('clientRef', '==', `clients/${clientId}`)
          .limit(1),
        {
          maxRefDepth: 0,
        },
      ),
    ),
    unbindClientReport: firestoreAction(({ unbindFirestoreRef }) => {
      unbindFirestoreRef('clientReport');
    }),
    bindDefaultExport: firestoreAction(({ bindFirestoreRef }) =>
      bindFirestoreRef(
        'defaultExport',
        db.collection('exports').doc('default'),
      ),
    ),
    unbindDefaultExport: firestoreAction(({ unbindFirestoreRef }) => {
      unbindFirestoreRef('defaultExport');
    }),
    bindReports: firestoreAction(({ bindFirestoreRef }) =>
      bindFirestoreRef(
        'reports',
        db.collection('widgets').orderBy('clientName'),
        {
          maxRefDepth: 0,
        },
      ),
    ),
    unbindReports: firestoreAction(({ unbindFirestoreRef }) => {
      unbindFirestoreRef('widgets');
    }),
    bindReportErrors: firestoreAction(({ bindFirestoreRef }) =>
      bindFirestoreRef(
        'reportErrors',
        db.collection('widget-import-errors').orderBy('clientName'),
      ),
    ),
    unbindReportErrors: firestoreAction(({ unbindFirestoreRef }) => {
      unbindFirestoreRef('widget-import-errors');
    }),
    bindSurvey: firestoreAction(({ bindFirestoreRef }, { surveyId }) =>
      bindFirestoreRef('survey', db.collection('surveys').doc(surveyId), {
        maxRefDepth: 0,
      }),
    ),
    unbindSurvey: firestoreAction(({ unbindFirestoreRef }) => {
      unbindFirestoreRef('survey');
    }),
    bindSurveyTemplates: firestoreAction(({ bindFirestoreRef }) =>
      bindFirestoreRef('surveyTemplates', db.collection('survey-templates')),
    ),
    unbindSurveyTemplates: firestoreAction(({ unbindFirestoreRef }) =>
      unbindFirestoreRef('survey-templates'),
    ),
    bindUnmappedDatapoints: firestoreAction(({ bindFirestoreRef }) =>
      bindFirestoreRef(
        'unmappedDatapoints',
        db.collection('unmapped-question-labels-to-datapoints').doc('default'),
      ),
    ),
    unbindUnmappedDatapoints: firestoreAction(({ unbindFirestoreRef }) => {
      unbindFirestoreRef('unmappedDatapoints');
    }),
    bindUnmappedQuestionTypes: firestoreAction(({ bindFirestoreRef }) =>
      bindFirestoreRef(
        'unmappedQuestionTypes',
        db.collection('unmapped-datapoints-to-question-types').doc('default'),
      ),
    ),
    unbindUnmappedQuestionTypes: firestoreAction(({ unbindFirestoreRef }) => {
      unbindFirestoreRef('unmappedQuestionTypes');
    }),
    addClient(context, { clientName }) {
      return db.collection('clients').add({
        name: clientName,
      });
    },
    async addSurvey(context, { templateId, clientId }) {
      await holisticFunctions.addClientSurvey({ templateId, clientId });
    },
    async mapUnmappedDatapoints(context, { mapping }) {
      await updateUnmapped({
        mapping,
        lookupCollection: 'survey-monkey-lookup-datapoint',
        unmappedCollection: 'unmapped-question-labels-to-datapoints',
      });
    },
    async mapUnmappedQuestionTypes(context, { mapping }) {
      await updateUnmapped({
        mapping,
        lookupCollection: 'survey-monkey-lookup-question-type',
        unmappedCollection: 'unmapped-datapoints-to-question-types',
      });
    },
    updateAnswer(context, { answer, questionId, surveyId }) {
      return db
        .collection('surveys')
        .doc(surveyId)
        .update({
          [`questions.${questionId}.answer`]: answer,
        });
    },
    updateDeclinedQuestion(context, { declined, questionId, surveyId }) {
      return db
        .collection('surveys')
        .doc(surveyId)
        .update({
          [`questions.${questionId}.declined`]: declined,
          [`questions.${questionId}.answer`]: null,
        });
    },
    updateNote(context, { note, questionId, surveyId }) {
      return db
        .collection('surveys')
        .doc(surveyId)
        .update({
          [`questions.${questionId}.note`]: note,
        });
    },
  },
});
